/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2020 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the property of
 * Pearson Education, Inc. The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S.
 * and Foreign Patents, patent applications, and are protected by trade secret
 * or copyright law. Dissemination of this information, reproduction of this
 * material, and copying or distribution of this software is strictly forbidden
 * unless prior written permission is obtained from Pearson Education, Inc.
 */

import { axiosFetch } from './axiosFetch';
import API_CONSTANTS from '../constants/apiConstants';
import Constants from '../constants';
import { getHeaders, postHeaders, deleteHeaders } from '../utils';

const { ERROR_CODE_5XX_MESSAGE, DIALOG_TITLE_ERROR } = Constants;

// Includes 500 because the upload API returns validation errors (e.g. invalid ISO
// codes) as 500 with details.message. Remove 500 once the API uses 400/422.
const UPLOAD_SKIP_ERROR_CODES = [400, 422, 500];

/**
 * User-facing message from glossary upload API error JSON.
 * Nested shape (e.g. invalid ISO codes): details.message is the specific text;
 * root message is often generic ("Bad Request").
 */
export const glossaryUploadErrorMessage = body => {
  if (!body || typeof body !== 'object') {
    return null;
  }
  const { details, message, error } = body;
  if (
    details &&
    typeof details === 'object' &&
    typeof details.message === 'string' &&
    details.message.trim()
  ) {
    return details.message.trim();
  }
  if (typeof message === 'string' && message.trim()) {
    return message.trim();
  }
  if (typeof error === 'string' && error.trim()) {
    return error.trim();
  }
  return null;
};

export const getGlossaryUploadGlobalErrorDialogContent = ({
  message,
  correlationId = ''
}) => ({
  message: `${message || ERROR_CODE_5XX_MESSAGE}
correlationId: ${correlationId}`,
  title: DIALOG_TITLE_ERROR
});

const messageFromUploadFailure = err => {
  const fromBody = glossaryUploadErrorMessage(err?.response?.data);
  if (fromBody) {
    return fromBody;
  }
  if (
    err?.message &&
    !/^Request failed with status code \d+$/.test(err.message)
  ) {
    return err.message;
  }
  return err?.message || 'Failed to upload glossary';
};

// Validation text for misclassified 500 responses; remove when API uses 400/422.
const glossaryUploadDetailsMessage = body => {
  const detailsMessage = body?.details?.message;
  if (typeof detailsMessage === 'string' && detailsMessage.trim()) {
    return detailsMessage.trim();
  }
  return null;
};

const resolveUploadErrorResult = (response, data) => {
  const { status } = response;
  const correlationId = response.config?.headers?.['x-correlation-id'] || '';
  const detailsMessage = glossaryUploadDetailsMessage(data);

  // Upload API returns client validation failures as 500 + details.message today.
  // Split inline vs global handling here until those use 400/422 instead.
  if (status === 500) {
    if (detailsMessage) {
      return { success: false, inlineMessage: detailsMessage };
    }
    return {
      success: false,
      showGlobalError: true,
      status,
      message: data?.message,
      correlationId
    };
  }

  const apiMessage = glossaryUploadErrorMessage(data);
  if (apiMessage) {
    return { success: false, inlineMessage: apiMessage };
  }

  return {
    success: false,
    showGlobalError: true,
    status,
    message: data?.message,
    correlationId
  };
};

export const getGlossaries = async () => {
  const apiPath = API_CONSTANTS.GET_GLOSSARIES;
  const reqOptions = {
    ...getHeaders()
  };

  const response = await axiosFetch(reqOptions, apiPath);
  return response.data;
};

export const uploadGlossary = async glossaryFile => {
  const apiPath = API_CONSTANTS.UPLOAD_GLOSSARY;

  // Validate that file is provided
  if (!glossaryFile) {
    throw new Error('Glossary file is required');
  }

  const formData = new FormData();
  formData.append('glossary_file', glossaryFile);

  const reqOptions = {
    ...postHeaders({
      'Content-Type': undefined // Let browser set multipart/form-data with boundary
    }),
    body: formData
  };

  // Skips interceptor for 400/422/500. 500 is included only because validation
  // errors are misclassified; drop it from codes once the API is corrected.
  const errorHandlingProps = {
    skipError: true,
    codes: UPLOAD_SKIP_ERROR_CODES
  };

  try {
    const response = await axiosFetch(
      reqOptions,
      apiPath,
      {},
      errorHandlingProps
    );
    const data = response.data ? { ...response.data } : {};

    if (UPLOAD_SKIP_ERROR_CODES.includes(response.status)) {
      return resolveUploadErrorResult(response, data);
    }

    if (data && data.success === false) {
      const apiMessage = glossaryUploadErrorMessage(data);
      return {
        success: false,
        inlineMessage: apiMessage || 'Upload failed'
      };
    }

    return data;
  } catch (err) {
    throw new Error(messageFromUploadFailure(err));
  }
};

export const removeGlossary = async glossaryId => {
  const apiPath = API_CONSTANTS.REMOVE_GLOSSARY;

  // Validate that glossary ID is provided
  if (!glossaryId) {
    throw new Error('Glossary ID is required');
  }

  const reqOptions = {
    ...deleteHeaders(),
    body: JSON.stringify({ glossaryId })
  };

  const response = await axiosFetch(reqOptions, apiPath);
  return response.data;
};
